import React, { useEffect, useState } from "react";
import { api } from "../../services/api";
import { useContext } from "react";
import AuthContext from "../../contexts/AuthContext";

interface PartnerOrder {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  coupon_code: string;
  total_amount: number;
  status: string;
  order_date: string;
  items_count: number;
}

interface CouponInfo {
  code: string;
  value: number;
  type: "fixed" | "percentage";
  commission_percentage: number;
}

export default function PartnerOrders() {
  const { user } = useContext(AuthContext) || {};
  const [orders, setOrders] = useState<PartnerOrder[]>([]);
  const [coupon, setCoupon] = useState<CouponInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    loadPartnerOrders(1);
  }, [user]);

  const loadPartnerOrders = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      // This endpoint would need to be created to get orders by coupon code associated with the partner
      const response: any = await api.get(
        `/partnerships.php?action=partner-orders&page=${page}&limit=20`,
      );

      if (response.success) {
        console.log("=== FULL API RESPONSE ===");
        console.log("Orders received:", response.orders);
        console.log("Debug info:", response.debug);
        console.log("Matching coupons:", response.debug?.matching_coupons);
        console.log("========================");

        setOrders(response.orders);
        setPagination(response.pagination);
        if (response.coupon) {
          setCoupon(response.coupon);
        }
      } else {
        throw new Error(response.message || "Failed to load orders");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      completed: "success",
      processing: "info",
      pending: "warning",
      cancelled: "danger",
      refunded: "secondary",
    };
    return `bg-${statusMap[status] || "secondary"}`;
  };

  if (!user) {
    return (
      <div className="alert alert-warning">
        You must be logged in to view partner orders.
      </div>
    );
  }

  if (user.role !== "partner") {
    return (
      <div className="alert alert-danger">
        You do not have permission to view partner orders.
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="h3">Your Coupon Orders</h1>
          <p className="text-muted">
            View all orders using your partnership coupon code
          </p>
        </div>
      </div>

      {/* Coupon Information Card */}
      {coupon && (
        <div className="card mb-4 border-primary">
          <div className="card-body">
            <h5 className="card-title mb-3">
              <i className="fa-solid fa-ticket me-2"></i>
              Your Partnership Coupon
            </h5>
            <div className="row align-items-center">
              <div className="col-md-4">
                <div className="d-flex align-items-center mb-2">
                  <span className="text-muted me-2">Coupon Code:</span>
                  <code className="fs-5 fw-bold bg-light px-3 py-2 rounded">
                    {coupon.code}
                  </code>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-center">
                  <span className="text-muted me-2">Discount Value:</span>
                  <span className="fs-5 fw-bold text-success">
                    {coupon.type === "percentage"
                      ? `${coupon.value}%`
                      : `$${coupon.value.toFixed(2)}`}
                    <span className="text-muted fs-6 ms-2">
                      (
                      {coupon.type === "percentage"
                        ? "Percentage"
                        : "Fixed Amount"}
                      )
                    </span>
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-center">
                  <span className="text-muted me-2">Your Commission:</span>
                  <span className="fs-5 fw-bold text-primary">
                    {coupon.commission_percentage}%
                    <span className="text-muted fs-6 ms-2">
                      (of discounted total)
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : orders.length === 0 ? (
        <div className="alert alert-info">
          No orders found with your coupon code yet.
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Coupon Code</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Your Commission</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const isDraft =
                    order.status === "draft" || order.total_amount === 0;
                  const commission =
                    !isDraft && coupon
                      ? (order.total_amount * coupon.commission_percentage) /
                        100
                      : 0;
                  return (
                    <tr key={order.id} className={isDraft ? "opacity-50" : ""}>
                      <td className="fw-medium">#{order.order_number}</td>
                      <td>
                        {isDraft ? (
                          <span className="text-muted fst-italic">
                            Incomplete order
                          </span>
                        ) : (
                          <>
                            <div>{order.customer_name}</div>
                            <small className="text-muted">
                              {order.customer_email}
                            </small>
                          </>
                        )}
                      </td>
                      <td>
                        <code>{order.coupon_code}</code>
                      </td>
                      <td>{isDraft ? "-" : order.items_count}</td>
                      <td className="fw-medium">
                        {isDraft ? (
                          <span className="text-muted">-</span>
                        ) : (
                          `$${order.total_amount.toFixed(2)}`
                        )}
                      </td>
                      <td className="fw-medium text-primary">
                        {isDraft ? (
                          <span className="text-muted">-</span>
                        ) : (
                          `$${commission.toFixed(2)}`
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge text-white ${getStatusBadge(order.status)}`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td>
                        <small>
                          {new Date(order.order_date).toLocaleDateString()}
                        </small>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <nav aria-label="Page navigation" className="mt-4">
              <ul className="pagination">
                <li
                  className={`page-item ${pagination.page === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => loadPartnerOrders(pagination.page - 1)}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: pagination.pages }).map((_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${pagination.page === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => loadPartnerOrders(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${pagination.page === pagination.pages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => loadPartnerOrders(pagination.page + 1)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}

          <div className="mt-4">
            <div className="card bg-light">
              <div className="card-body">
                <h5 className="card-title">Summary</h5>
                <div className="row">
                  <div className="col-md-3">
                    <p className="text-muted mb-1">Total Orders</p>
                    <h4 className="mb-0">
                      {orders.filter((o) => o.total_amount > 0).length}
                    </h4>
                    {orders.filter((o) => o.total_amount === 0).length > 0 && (
                      <small className="text-muted">
                        ({orders.filter((o) => o.total_amount === 0).length}{" "}
                        incomplete)
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <p className="text-muted mb-1">Revenue Generated</p>
                    <h4 className="mb-0">
                      $
                      {orders
                        .filter((o) => o.total_amount > 0)
                        .reduce((sum, order) => sum + order.total_amount, 0)
                        .toFixed(2)}
                    </h4>
                  </div>
                  <div className="col-md-3">
                    <p className="text-muted mb-1">Total Commission Earned</p>
                    <h4 className="mb-0 text-primary">
                      $
                      {coupon
                        ? orders
                            .filter((o) => o.total_amount > 0)
                            .reduce(
                              (sum, order) =>
                                sum +
                                (order.total_amount *
                                  coupon.commission_percentage) /
                                  100,
                              0,
                            )
                            .toFixed(2)
                        : "0.00"}
                    </h4>
                  </div>
                  <div className="col-md-3">
                    <p className="text-muted mb-1">Average Order Value</p>
                    <h4 className="mb-0">
                      $
                      {orders.filter((o) => o.total_amount > 0).length > 0
                        ? (
                            orders
                              .filter((o) => o.total_amount > 0)
                              .reduce(
                                (sum, order) => sum + order.total_amount,
                                0,
                              ) /
                            orders.filter((o) => o.total_amount > 0).length
                          ).toFixed(2)
                        : "0.00"}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
